import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { SelectList } from "@common/selectList";
import { Calendar } from "@common/calendar";
import { Props } from "./types";
import styles from "./styles.scss";

// Noon is the safest default time for a date-only pick: no DST transition
// ever crosses noon, so the displayed date is stable across timezones.
const DEFAULT_HOUR = 12;

function DateTimeInput({
  dateTime,
  projectTimezone,
  placeholder,
  isInvalid,
  isDisabled,
  readOnly,
  input,
}: Props) {
  const { t } = useTranslation();
  const zone = projectTimezone || DateTime.local().zoneName;
  const display = dateTime
    ? DateTime.fromISO(dateTime, { zone: "utc" }).setZone(zone)
    : null;
  const isValid = !!display && display.isValid;

  const commitDateOnly = (year: number, month: number, day: number) => {
    // Preserve the existing hour/minute in project TZ when editing; for a
    // fresh pick with no prior time, default to noon (see DEFAULT_HOUR).
    const hour = isValid ? display.hour : DEFAULT_HOUR;
    const minute = isValid ? display.minute : 0;
    const next = DateTime.fromObject(
      { year, month, day, hour, minute, second: 0, millisecond: 0 },
      { zone },
    );
    if (!next.isValid) return;
    input(next.toUTC().toISO() ?? "");
  };

  return readOnly ? (
    <div
      className={classNames(styles.dateTimeInput, {
        [styles.invalid]: isInvalid,
        [styles.disabled]: isDisabled,
      })}
      title={t("dateTimeInput.projectTimezoneTitle", { timezone: zone })}
    >
      <div className={styles.calendarInput}>
        <div className={styles.input}>
          {isValid ? (
            <p className={styles.value}>{display.toFormat("dd/MM/yyyy")}</p>
          ) : (
            <p className={styles.placeholder}>{placeholder}</p>
          )}
        </div>
      </div>
      <div className={styles.input}>
        {isValid ? (
          <p className={styles.value}>{display.toFormat("HH:mm")}</p>
        ) : (
          <p className={styles.placeholder}>00:00</p>
        )}
      </div>
    </div>
  ) : (
    <div
      className={classNames(styles.dateTimeInput, {
        [styles.invalid]: isInvalid,
      })}
      title={t("dateTimeInput.projectTimezoneTitle", { timezone: zone })}
    >
      <SelectList<string>
        rootClassName={classNames(styles.calendarInput, {
          [styles.invalid]: isInvalid,
          [styles.disabled]: isDisabled,
        })}
        listClassName={styles.calendar}
      >
        {({ select }) => ({
          input: (
            <div className={styles.input}>
              {isValid ? (
                <p className={styles.value}>{display.toFormat("dd/MM/yyyy")}</p>
              ) : (
                <p className={styles.placeholder}>{placeholder}</p>
              )}
            </div>
          ),
          list: (
            <Calendar
              date={
                isValid
                  ? {
                      year: display.year,
                      month: display.month - 1,
                      day: display.day,
                    }
                  : (() => {
                      const now = DateTime.now().setZone(zone);
                      return {
                        year: now.year,
                        month: now.month - 1,
                        day: now.day,
                      };
                    })()
              }
              editDate={({ year, month, day }) => {
                commitDateOnly(year, month + 1, day);
                select("");
              }}
              clear={() => input(select(""))}
            />
          ),
        })}
      </SelectList>
    </div>
  );
}

export { DateTimeInput };
